import React, { type ForwardedRef, type PropsWithChildren } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import {
  CustomTreeData,
  DataTypeProvider,
  TreeDataState,
  type GridColumnExtension,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  Table,
  TableHeaderRow,
} from "@devexpress/dx-react-grid-bootstrap4";
import { useMMMTableExpandedRows } from "@features/mmm/hooks/mmm-url-state";
import { type BudgetMixBreakdownSpendDatum } from "@north-beam/nb-common";

import { BUDGET_MIX_WIDTH_WITH_CHART } from "../budget-mix-chart/constants";

import { MixCellFormatter } from "./mix-cell-formatter";
import { SpendCellFormatter } from "./spend-cell-formatter";
import { getChildRows, getRows } from "./utils";

import "./mmm-budget-mix-table.scss";

type Props = {
  tableData: BudgetMixBreakdownSpendDatum[];
};

export type FormattedBreakdownTableRow = BudgetMixBreakdownSpendDatum & {
  spend: string;
  mix: string;
  label: string;
  items?: FormattedBreakdownTableRow[];
};

export const BudgetMixTable = ({ tableData }: Props) => {
  const mixColumnSize = 76;
  const { user } = useUser();
  const [expandedRows] = useMMMTableExpandedRows();

  const columns = [
    { name: "mix", title: "Mix %" },
    { name: "spend", title: "Spend" },
  ];

  const columnExtensions: GridColumnExtension[] = [
    { columnName: "mix", width: mixColumnSize, align: "right" },
    {
      columnName: "spend",
      width: BUDGET_MIX_WIDTH_WITH_CHART - mixColumnSize,
      align: "right",
    },
  ];

  const rows = getRows(tableData, user);

  return (
    <div className="w-full h-full p-0 m-0 mmm-budget-mix-table [&_.dx-g-bs4-header-cell:first-child]:border-l-0">
      <Grid rows={rows} columns={columns}>
        <TreeDataState expandedRowIds={expandedRows} />
        <DataTypeProvider for={["mix"]} formatterComponent={MixCellFormatter} />
        <DataTypeProvider
          for={["spend"]}
          formatterComponent={SpendCellFormatter}
        />
        <CustomTreeData getChildRows={getChildRows} />
        <Table columnExtensions={columnExtensions} cellComponent={TableCell} />
        <TableHeaderRow />
      </Grid>
    </div>
  );
};

interface TableCellProps extends PropsWithChildren<Table.CellProps> {
  forwardedRef?: ForwardedRef<HTMLTableCellElement>;
}

const TableCell = ({
  children,
  forwardedRef,
  tableRow: { row },
  tableColumn: { column },
}: TableCellProps) => (
  <td
    ref={forwardedRef}
    className={clsx("dx-g-bs4-table-cell text-right ", {
      "border-l-0": column?.name !== "spend",
      "bg-v2-secondary": !Object.hasOwn(row, "items"),
    })}
  >
    {children}
  </td>
);
